import React, { useState, useCallback } from 'react';
import { pollsApi } from '../../api/polls';
import HrnbModal from '../controls/HrnbModal';
import HrnbButton from '../controls/HrnbButton';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbAlert from '../controls/HrnbAlert';
import '../../css/chats/CreatePollModal.css';

const MAX_OPTIONS = 10;
const MIN_OPTIONS = 2;

/**
 * Модальное окно создания опроса в групповом чате
 *
 * @param {Object} props
 * @param {boolean} props.open - Флаг открытия
 * @param {Function} props.onClose - Закрытие модалки
 * @param {Function} props.onCreated - Колбэк при успешном создании опроса (poll) => void
 * @param {string} props.chatId - ID чата (UUID)
 */
export default function CreatePollModal({ open, onClose, onCreated, chatId }) {
    const [question, setQuestion] = useState('');
    const [options, setOptions] = useState(['', '']);
    const [multipleChoice, setMultipleChoice] = useState(false);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);

    const handleClose = useCallback(() => {
        if (creating) return;
        // Сброс состояния
        setQuestion('');
        setOptions(['', '']);
        setMultipleChoice(false);
        setError(null);
        onClose?.();
    }, [creating, onClose]);

    const handleQuestionChange = useCallback((e) => {
        setQuestion(e.target.value);
        setError(null);
    }, []);

    const handleOptionChange = useCallback((index, value) => {
        setOptions(prev => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
        setError(null);
    }, []);

    const addOption = useCallback(() => {
        setOptions(prev => {
            if (prev.length >= MAX_OPTIONS) return prev;
            return [...prev, ''];
        });
    }, []);

    const removeOption = useCallback((index) => {
        setOptions(prev => {
            if (prev.length <= MIN_OPTIONS) return prev;
            return prev.filter((_, i) => i !== index);
        });
    }, []);

    /**
     * Валидация формы перед отправкой
     */
    const validate = useCallback(() => {
        const trimmedQuestion = question.trim();
        if (!trimmedQuestion) {
            setError('Введите вопрос опроса');
            return false;
        }
        if (trimmedQuestion.length > 500) {
            setError('Вопрос не может быть длиннее 500 символов');
            return false;
        }

        const trimmedOptions = options.map(o => o.trim()).filter(o => o.length > 0);
        if (trimmedOptions.length < MIN_OPTIONS) {
            setError(`Нужно минимум ${MIN_OPTIONS} варианта ответа`);
            return false;
        }

        // Проверка на уникальность (case-insensitive)
        const unique = new Set(trimmedOptions.map(o => o.toLowerCase()));
        if (unique.size !== trimmedOptions.length) {
            setError('Варианты ответов должны быть уникальными');
            return false;
        }

        // Проверка длины каждого варианта
        for (const opt of trimmedOptions) {
            if (opt.length > 300) {
                setError('Вариант ответа не может быть длиннее 300 символов');
                return false;
            }
        }

        return true;
    }, [question, options]);

    const handleCreate = useCallback(async () => {
        if (!validate() || creating || !chatId) return;

        setCreating(true);
        setError(null);

        try {
            const trimmedOptions = options
                .map(o => o.trim())
                .filter(o => o.length > 0);

            const poll = await pollsApi.create(chatId, {
                question: question.trim(),
                multipleChoice,
                options: trimmedOptions.map(text => ({ text })),
            });

            // Сброс формы
            setQuestion('');
            setOptions(['', '']);
            setMultipleChoice(false);
            onCreated?.(poll);
            onClose?.();
        } catch (err) {
            console.error('[CreatePollModal] Create failed:', err);
            const message = err.response?.data?.message
                || err.message
                || 'Не удалось создать опрос';
            setError(message);
        } finally {
            setCreating(false);
        }
    }, [chatId, question, options, multipleChoice, validate, creating, onCreated, onClose]);

    const emptyOptions = options.filter(o => o.trim()).length;
    const canAddOption = options.length < MAX_OPTIONS;
    const canRemoveOption = options.length > MIN_OPTIONS;

    return (
        <HrnbModal
            open={open}
            onClose={handleClose}
            title="Создать опрос"
        >
            <div className="create-poll-modal">
                {error && (
                    <HrnbAlert variant="danger" onClose={() => setError(null)}>
                        {error}
                    </HrnbAlert>
                )}

                {/* Вопрос */}
                <div className="create-poll-modal__field">
                    <label className="create-poll-modal__label" htmlFor="poll-question">
                        Вопрос
                    </label>
                    <input
                        id="poll-question"
                        type="text"
                        className="create-poll-modal__input"
                        placeholder="Введите вопрос опроса..."
                        value={question}
                        onChange={handleQuestionChange}
                        maxLength={500}
                        disabled={creating}
                        autoFocus
                    />
                </div>

                {/* Варианты ответов */}
                <div className="create-poll-modal__field">
                    <label className="create-poll-modal__label">
                        Варианты ответов
                        <span className="create-poll-modal__hint">
                            ({options.length} / {MAX_OPTIONS})
                        </span>
                    </label>
                    <div className="create-poll-modal__options">
                        {options.map((option, index) => (
                            <div key={index} className="create-poll-modal__option-row">
                                <input
                                    type="text"
                                    className="create-poll-modal__input"
                                    placeholder={`Вариант ${index + 1}`}
                                    value={option}
                                    onChange={(e) => handleOptionChange(index, e.target.value)}
                                    maxLength={300}
                                    disabled={creating}
                                />
                                {canRemoveOption && (
                                    <button
                                        type="button"
                                        className="create-poll-modal__remove-btn"
                                        onClick={() => removeOption(index)}
                                        disabled={creating}
                                        title="Удалить вариант"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    {canAddOption && (
                        <button
                            type="button"
                            className="create-poll-modal__add-btn"
                            onClick={addOption}
                            disabled={creating}
                        >
                            + Добавить вариант
                        </button>
                    )}
                </div>

                {/* Множественный выбор */}
                <div className="create-poll-modal__field">
                    <label className="create-poll-modal__checkbox-label">
                        <input
                            type="checkbox"
                            checked={multipleChoice}
                            onChange={(e) => setMultipleChoice(e.target.checked)}
                            disabled={creating}
                            className="create-poll-modal__checkbox"
                        />
                        <span>Разрешить множественный выбор</span>
                    </label>
                </div>

                {/* Кнопки */}
                <div className="create-poll-modal__actions">
                    <HrnbButton
                        variant="secondary"
                        onClick={handleClose}
                        disabled={creating}
                    >
                        Отмена
                    </HrnbButton>
                    <HrnbButton
                        onClick={handleCreate}
                        disabled={creating || emptyOptions < MIN_OPTIONS || !question.trim()}
                    >
                        {creating ? <HrnbLoader size="sm" /> : 'Создать опрос'}
                    </HrnbButton>
                </div>
            </div>
        </HrnbModal>
    );
}